import React, { useRef } from 'react'
import { Switch, Redirect, RouteComponentProps } from 'react-router-dom'

import {
  GenericErrorView,
  routes,
  RouteParams,
  useGetWorkspaces,
  useFlag,
} from '@sketch/modules-common'

import { removeItem, useNagivationScrollTop } from '@sketch/utils'

import { localStorageKeys } from '@sketch/constants'

import { useIsProfileCreated } from '../../../community/hooks'
import {
  useWorkspaceSettings,
  useRefetchWorkspaceSettingsOnRouteChange,
} from '../../operations'

import { BillingStatusBanners } from '../../components'

import {
  WorkspaceSettingsBilling,
  WorkspaceSettingsSso,
  WorkspaceSettingsEncryption,
  WorkspaceSettingsIntegrations,
  WorkspaceSettingsIntegrationsIdentity,
} from '../../pages'

import { PanelWrapper } from './WorkspaceSettingsView.styles'

// Pages
import { PersonalWorkspaceSettingsPeople } from '../../pages/WorkspaceSettingsPeople'
import { WorkspaceSettingsProfile } from '../../pages/WorkspaceSettingsProfile'
import { WorkspaceSettingsDocumentPreviews } from '../../pages/WorkspaceSettingsDocumentPreviews'
import { WorkspaceSettingsAuditLog } from '../../pages/WorkspaceSettingsAuditLog'
import WorkspaceSettingsPermissionGroups from '../../pages/WorkspaceSettingsPermissionGroups'
import { WorkspaceSettingsPeopleContextProvider } from './WorkspaceSettingsPeopleContext'
import {
  CloudBillingPlanFragment,
  useGetWorkspaceMembershipsQuery,
  useGetWorkspaceStorageDetailsQuery,
} from '@sketch/gql-types'
import {
  workspaceSettingsPermissions,
  useCanSubscribeViaStripe,
  isAppStoreWorkspace,
} from '../../utils'
import { CanRenderOptions } from '../../types'

import { LoadingState } from '@sketch/components'

import { WorkspaceSidebarLayoutExtraProps } from '../../components/WorkspaceSidebarLayout/WorkspaceSidebarLayout'

import { WorkspaceSettingsLayout } from '../../components/WorkspaceSettingsLayout'
import { RestrictedWorkspaceSettingsRoute } from '../../containers/RestrictedWorkspaceSettingsRoute'

export type WorkspaceSettingsRoutesProps = RouteComponentProps<
  RouteParams<'WORKSPACE_SETTINGS'>
>

type WorkspaceSettingsProps = WorkspaceSettingsRoutesProps &
  WorkspaceSidebarLayoutExtraProps

const WorkspaceSettingsPeople = React.lazy(
  () => import('../../pages/WorkspaceSettingsPeople')
)
const WorkspaceSettingsGeneral = React.lazy(
  () => import('../../pages/WorkspaceSettingsGeneral')
)
const WorkspaceSettingsDocuments = React.lazy(
  () => import('../../pages/WorkspaceSettingsDocuments')
)
const WorkspaceSettingsBillingMAS = React.lazy(
  () => import('../../pages/WorkspaceSettingsBillingMAS')
)

/**
 * WorkspaceSettings
 *
 * This is the base component for the Workspace settings views
 */
const WorkspaceSettingsView: React.FCC<WorkspaceSettingsProps> = props => {
  const elementToScrollTop = useRef<HTMLElement | null>(null)

  const isIntegrationsEnabled = useFlag('workspace-integrations')
  const isSlackMentionsEnabled = useFlag('slack-mention-notifications')
  const isAuditLogsEnabled = useFlag('audit-log')

  const { workspace } = props
  const { identifier, avatar, name } = workspace
  const workspaceId = workspace.identifier
  const isAdmin = workspace.userRole === 'ADMIN'

  const { isProfileCreated } = useIsProfileCreated(workspace.identifier)

  const {
    workspaces,
    loading: workspacesLoading,
    error: hasWorkspacesError,
  } = useGetWorkspaces()

  const {
    data: workspaceSettingsData,
    loading: isWorkspaceSettingsLoading,
    updating: isUpdatingWorkspaceSettings,
    error: hasWorkspaceSettingsError,
    refetch,
  } = useWorkspaceSettings(workspace)

  useRefetchWorkspaceSettingsOnRouteChange(refetch, workspaceId)

  const {
    workspace: workspaceSettings,
    customer,
    sso,
  } = workspaceSettingsData || {}

  const { source, customerProvider } = useCanSubscribeViaStripe(
    identifier,
    customer?.id
  )

  const {
    hideNavigation,
    isEducationWorkspace,
    isBusinessWorkspace,
    isEnterpriseWorkspace,
    isFinance,
    isIOS,
    isPartner,
    isPersonalWorkspace,
    isGuest,
    showUpsell,
    showWorkspaceSettingsBilling,
    showWorkspaceSettingsGeneral,
    showWorkspaceSettingsSSO,
    showWorkspaceSettingsAuditLogs,
    showWorkspaceSettingsEncryption,
    showWorkspaceSettingsPeople,
    showWorkspacePermissionGroup,
    showWorkspaceSettingsProfile,
    showDocumentPreviews,
    showWorkspaceSettingsIntegrations,
    showWorkspaceSettingsConnectedAccounts,
  } = workspaceSettingsPermissions({
    workspace,
    customerProvider,
    settings: workspaceSettings,
    customer,
    source,
    isProfileCreated,
    isIntegrationsEnabled,
    isSlackMentionsEnabled,
    isAuditLogsEnabled,
  })

  // Menu permissions for Workspace Layout
  const canRender: CanRenderOptions = {
    generalMenu: showWorkspaceSettingsGeneral,
    billingMenu: showWorkspaceSettingsBilling,
    permissionGroups: showWorkspacePermissionGroup,
    SSOMenu: showWorkspaceSettingsSSO,
    AuditLogsMenu: showWorkspaceSettingsAuditLogs,
    peopleMenu: showWorkspaceSettingsPeople,
    profileMenu: showWorkspaceSettingsProfile,
    documentPreviews: showDocumentPreviews,
    encryptionMenu: showWorkspaceSettingsEncryption,
    integrationsMenu: showWorkspaceSettingsIntegrations,
    connectedAccountsMenu: showWorkspaceSettingsConnectedAccounts,
  }

  // Check for Sketch Partner on the workspace
  const { data: sketchPartnerData, loading: isLoadingPartnerMembership } =
    useGetWorkspaceMembershipsQuery({
      variables: {
        workspaceId: workspaceId,
        role: 'PARTNER',
      },
      skip: isPartner,
    })

  const {
    data: storageData,
    loading: isLoadingStorage,
    error: hasStorageError,
  } = useGetWorkspaceStorageDetailsQuery({
    variables: {
      identifier: workspaceId,
    },
    fetchPolicy: 'network-only',
    skip: !canRender.billingMenu,
  })

  const loading =
    workspacesLoading ||
    isWorkspaceSettingsLoading ||
    isLoadingPartnerMembership ||
    isLoadingStorage

  const error =
    hasWorkspacesError || hasWorkspaceSettingsError || hasStorageError

  useNagivationScrollTop(elementToScrollTop.current)

  if (isGuest) {
    return <Redirect to={routes.WORKSPACE_SHARES.create({ workspaceId })} />
  }

  /**
   * Because workspace and workspaceSettings are fetched in two different queries,
   * after upgrading a legacy workspace, the user can be in a situation where both
   * are out of sync in the cache. "workspace" already says that the workspace has been upgraded
   * but the settings is missing info we are expecting for upgraded workspaces (billing for example)
   * and we need to wait for it to be updated in the cache.
   * https://github.com/sketch-hq/Cloud/issues/8658
   */
  const isUpdatingWorkspaceSettingsInCacheAfterUpgrade =
    workspaceSettings && workspace.type !== workspaceSettings.type

  /**
   * Updates should not show the page spinner unless it's an important one that changes
   * the structure of the page (legacy workspace upgrade)
   */

  if (
    loading &&
    (!isUpdatingWorkspaceSettings ||
      isUpdatingWorkspaceSettingsInCacheAfterUpgrade)
  ) {
    return <LoadingState />
  }

  if (error || !workspaceSettings || !workspaces) {
    // This should be taken care by the PrivateWorkspaceRoute already
    // However, due to a different issue: https://github.com/sketch-hq/Cloud/issues/15006
    // The workspace which is invalid might bleed to a deeper component
    //
    // Remove this check when #15006 will be solved:
    removeItem(localStorageKeys.lastWorkspaceIdKey)
    return (
      <GenericErrorView
        error={error}
        isInLayout
        // Remove this when #15006 will be solved:
        onClick={() => (window.location.href = '/c')}
      />
    )
  }

  if (isPersonalWorkspace && !workspace.userIsOwner) {
    return (
      <GenericErrorView
        isInLayout
        errorTitle="Only the owner of this workspace can see its settings"
      />
    )
  }

  const {
    billing,
    identifier: customerId,
    ssoEnabled: ssoActive,
    ssoShortName,
  } = workspaceSettings.customer ?? {}

  const hasUnlimitedStorage = workspace.features.unlimitedStorageEnabled

  const { status, trialEnd, seats } = billing ?? {}

  const avatarSrc = avatar?.large || undefined
  const subscriptionEnd = billing?.subscriptionEnd || undefined

  const currentPlan = customer?.subscriptionInfo?.currentPlan || undefined

  const nextBillingCycleDate =
    customer?.subscriptionInfo?.renewalDate || undefined

  const pendingScaToken =
    customer?.subscriptionInfo.__typename === 'CloudSubscription' &&
    customer?.subscriptionInfo?.pendingScaToken

  const customerCurrency = customer?.billingSummary.current.currency
  const projectedCost = customer?.billingSummary?.next
  const credits = customer?.creditBalance
  const paymentDetails = customer?.paymentDetails

  const workspacePartner =
    sketchPartnerData?.workspace.memberships.list?.entries?.[0]

  return (
    <WorkspaceSettingsLayout
      hideNavbar={hideNavigation}
      canRender={canRender}
      {...props}
    >
      {status && (
        <BillingStatusBanners
          workspace={workspace}
          billingStatus={status}
          cancelPeriodEnd={subscriptionEnd}
          isEducationWorkspace={isEducationWorkspace}
        />
      )}
      <PanelWrapper>
        <Switch>
          <RestrictedWorkspaceSettingsRoute
            canAccess={showWorkspaceSettingsPeople}
            workspace={workspace}
            path={routes.WORKSPACE_SETTINGS_PEOPLE.template()}
            render={() => {
              if (isPersonalWorkspace) {
                /* There's already a FF inside there soo no need to touch it */
                return <PersonalWorkspaceSettingsPeople name={name} />
              }

              return (
                <WorkspaceSettingsPeopleContextProvider>
                  <WorkspaceSettingsPeople
                    workspaceId={identifier}
                    name={name}
                    avatar={avatarSrc}
                    role={workspace.userRole}
                    billingStatus={status!}
                    subscriptionEnd={subscriptionEnd}
                    seats={seats!}
                    /* Exclusive Administrative Props */
                    isBusinessWorkspace={isBusinessWorkspace}
                    isEnterpriseWorkspace={isEnterpriseWorkspace}
                    customerId={customerId!}
                    customerCurrency={customerCurrency!}
                    // These are optional because this page is common for both
                    // members and admins. This information is only available
                    // in PaymentQL which is queryable by admins only
                    currentPlan={currentPlan}
                    nextBillingCycleDate={nextBillingCycleDate}
                    paymentDetails={paymentDetails}
                    isSsoActive={ssoActive!}
                    isEducationWorkspace={isEducationWorkspace}
                    isIOS={isIOS}
                    showUpsell={showUpsell}
                    userDirectoryEnabled={
                      workspaceSettings.features.userDirectoryEnabled
                    }
                    unlimitedTrialEnabled={
                      workspaceSettings.features.unlimitedTrialEnabled
                    }
                    showWorkspacePermissionGroup={showWorkspacePermissionGroup}
                    source={source}
                  />
                </WorkspaceSettingsPeopleContextProvider>
              )
            }}
            exact
          />
          <RestrictedWorkspaceSettingsRoute
            canAccess={showWorkspaceSettingsGeneral}
            workspace={workspace}
            path={routes.WORKSPACE_SETTINGS_DOCUMENTS.template()}
            render={() => (
              <WorkspaceSettingsDocuments
                workspaceId={identifier}
                areUploadsRestricted={
                  workspaceSettings.uploadsRestrictedFor !== 'NONE'
                }
                arePublicLinksRestricted={
                  workspaceSettings.features.restrictedPublicLinks
                }
                canUseRestrictedUploads={
                  workspaceSettings.canUseRestrictedUploads
                }
              />
            )}
            exact
          />
          <RestrictedWorkspaceSettingsRoute
            canAccess={showWorkspaceSettingsGeneral}
            workspace={workspace}
            path={routes.WORKSPACE_SETTINGS_SETTINGS.template()}
            render={() => (
              <WorkspaceSettingsGeneral
                workspaceId={identifier}
                name={name}
                avatar={avatarSrc}
                userIsOwner={workspace.userIsOwner}
                securityEmail={workspaceSettings.securityEmail || undefined}
              />
            )}
            exact
          />
          <RestrictedWorkspaceSettingsRoute
            canAccess={showWorkspacePermissionGroup}
            workspace={workspace}
            path={routes.WORKSPACE_SETTINGS_PERMISSION_GROUPS.template()}
            render={() => (
              <WorkspaceSettingsPermissionGroups workspace={workspace} />
            )}
          />
          <RestrictedWorkspaceSettingsRoute
            workspace={workspace}
            canAccess={showWorkspaceSettingsBilling}
            path={routes.WORKSPACE_SETTINGS_BILLING.template()}
            render={() =>
              isAppStoreWorkspace(source) ? (
                <WorkspaceSettingsBillingMAS />
              ) : (
                <WorkspaceSettingsBilling
                  onWorkspaceSettingRefresh={refetch}
                  workspaceId={identifier}
                  workspaceName={name}
                  subscriptionStatus={status!}
                  subscriptionEnd={subscriptionEnd}
                  seats={seats!}
                  isFinance={isFinance}
                  isEducationWorkspace={isEducationWorkspace}
                  isProfessionalWorkspace={isBusinessWorkspace}
                  isEnterpriseWorkspace={isEnterpriseWorkspace}
                  isSsoActive={ssoActive!}
                  /* Exclusive Administrative Props */
                  credits={credits!}
                  customerId={customerId!}
                  customerCurrency={customerCurrency!}
                  currentPlan={currentPlan as CloudBillingPlanFragment}
                  pendingSCAToken={pendingScaToken || undefined}
                  nextBillingCycleDate={nextBillingCycleDate}
                  billingDetails={customer!.billingDetails!}
                  paymentDetails={paymentDetails}
                  projectedCost={projectedCost}
                  trialEnd={trialEnd!}
                  isPartner={isPartner}
                  showUpsell={showUpsell}
                  partner={workspacePartner ? workspacePartner : null}
                  // Storage
                  storageDetails={storageData}
                  hasUnlimitedStorage={hasUnlimitedStorage}
                  isAdmin={isAdmin}
                />
              )
            }
            exact
          />
          <RestrictedWorkspaceSettingsRoute
            canAccess={showWorkspaceSettingsSSO}
            workspace={workspace}
            path={routes.WORKSPACE_SETTINGS_SSO.template()}
            render={() => (
              <WorkspaceSettingsSso
                customerId={customerId!}
                workspaceId={identifier}
                ssoShortName={ssoShortName || undefined}
                isSsoActive={ssoActive!}
                isScimEnabled={workspaceSettings.scimEnabled}
                isScimAccessEnabled={
                  workspaceSettings.features.scimAccessEnabled
                }
                scimEndpoint={workspaceSettings.scimEndpoint || undefined}
                config={sso?.ssoConfig || undefined}
                showUpsell={showUpsell}
              />
            )}
            exact
          />
          {isAuditLogsEnabled && (
            <RestrictedWorkspaceSettingsRoute
              canAccess={showWorkspaceSettingsAuditLogs}
              workspace={workspace}
              path={routes.WORKSPACE_SETTINGS_AUDIT_LOGS.template()}
              render={() => (
                <WorkspaceSettingsAuditLog workspaceIdentifier={identifier} />
              )}
              exact
            />
          )}
          <RestrictedWorkspaceSettingsRoute
            canAccess={showWorkspaceSettingsEncryption}
            workspace={workspace}
            path={routes.WORKSPACE_SETTINGS_ENCRYPTION.template()}
            render={() => (
              <WorkspaceSettingsEncryption
                workspaceId={identifier}
                isEncryptionEnabled={
                  workspaceSettings.features.encryptionEnabled
                }
                roleArn={
                  workspaceSettings.encryptionConfig?.roleArn ?? undefined
                }
                encryptionKeys={workspaceSettings.encryptionConfig?.kmsArns?.filter(
                  e => e !== null
                )}
              />
            )}
            exact
          />
          <RestrictedWorkspaceSettingsRoute
            canAccess={isProfileCreated && isAdmin}
            workspace={workspace}
            path={routes.WORKSPACE_SETTINGS_PROFILE.template()}
            render={() => (
              <WorkspaceSettingsProfile workspaceIdentifier={workspaceId} />
            )}
            exact
          />
          <RestrictedWorkspaceSettingsRoute
            canAccess={showDocumentPreviews}
            workspace={workspace}
            path={routes.WORKSPACE_SETTINGS_DOCUMENT_PREVIEWS.template()}
            render={() => (
              <WorkspaceSettingsDocumentPreviews
                workspaceIdentifier={workspaceId}
              />
            )}
            exact
          />
          <RestrictedWorkspaceSettingsRoute
            canAccess={showWorkspaceSettingsIntegrations}
            workspace={workspace}
            path={routes.WORKSPACE_SETTINGS_INTEGRATIONS.template()}
            render={() => (
              <WorkspaceSettingsIntegrations workspaceId={workspaceId} />
            )}
            exact
          />
          <RestrictedWorkspaceSettingsRoute
            canAccess={showWorkspaceSettingsConnectedAccounts}
            workspace={workspace}
            path={routes.WORKSPACE_SETTINGS_CONNECTED_ACCOUNTS.template()}
            render={() => (
              <WorkspaceSettingsIntegrationsIdentity
                workspaceId={workspaceId}
                isAdmin={workspace.userCanAdminister}
              />
            )}
            exact
          />

          <Redirect
            from={routes.WORKSPACE_SETTINGS.template()}
            to={
              showWorkspaceSettingsPeople
                ? routes.WORKSPACE_SETTINGS_PEOPLE.create({
                    workspaceId,
                  })
                : showWorkspaceSettingsBilling
                  ? routes.WORKSPACE_SETTINGS_BILLING.create({ workspaceId })
                  : routes.WORKSPACE_SHARES.create({ workspaceId })
            }
          />
        </Switch>
      </PanelWrapper>
    </WorkspaceSettingsLayout>
  )
}

export default WorkspaceSettingsView
